import React, { useState, useEffect } from 'react';
import { Input, Table, Button, InputNumber, message, Select, Modal } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { WordPressAPI } from '../../services/api/api';
import type { ColumnsType } from 'antd/es/table';
import { WCProduct, OrderCapacityItem } from '../../types/wordpress';

interface OrderCapacityFormProps {
    orderFileId: number;
}

type SearchType = 'barcode' | 'name' | 'sku' | 'fda';

const OrderCapacityForm: React.FC<OrderCapacityFormProps> = ({ orderFileId }) => {
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState<WCProduct[]>([]);
    const [selectedProducts, setSelectedProducts] = useState<OrderCapacityItem[]>([]);
    const [searchType, setSearchType] = useState<SearchType>('barcode');
    const [isModalVisible, setIsModalVisible] = useState(false);

    useEffect(() => {
        if (searchText.length > 4) {
            handleSearch(searchText);
        } else {
            setProducts([]);
        }
    }, [searchText]);

    const handleSearch = async (value: string) => {
        if (!value || value.length < 3) return;

        setLoading(true);
        try {
            const response = await WordPressAPI.searchProducts(value, searchType);

            if (Array.isArray(response)) {
                if (response.length === 1) {
                    handleAddProduct(response[0]);
                    setProducts([]);
                } else if (response.length > 1) {
                    setProducts(response);
                    setIsModalVisible(true);
                } else {
                    message.info('No products found');
                    setProducts([]);
                }
            }
        } catch (error) {
            message.error('Failed to search products');
            console.error('Error searching products:', error);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddProduct = (product: WCProduct) => {
        if (!product.id) return;

        const exists = selectedProducts.find(p => p.product_id === product.id);
        if (!exists) {
            setSelectedProducts(prev => [
                ...prev,
                {
                    product_id: product.id,
                    product_name: product.name || 'Unknown Product',
                    quantity: 1
                }
            ]);
            setSearchText('');
            setIsModalVisible(false);
        } else {
            message.warning('Product already added');
        }
    };

    const handleQuantityChange = (productId: number, quantity: number) => {
        if (quantity < 1) return;

        setSelectedProducts(prev =>
            prev.map(item =>
                item.product_id === productId
                    ? { ...item, quantity }
                    : item
            )
        );
    };

    const handleRemoveProduct = (productId: number) => {
        setSelectedProducts(prev =>
            prev.filter(item => item.product_id !== productId)
        );
    };

    const handleSubmit = async () => {
        if (selectedProducts.length === 0) {
            message.warning('Please add at least one product');
            return;
        }

        setLoading(true);
        try {
            // await WordPressAPI.submitOrderCapacity(orderFileId, selectedProducts);
            message.success('Order capacity submitted successfully');
            setSelectedProducts([]); // Clear form after success
        } catch (error) {
            message.error('Failed to submit order capacity');
            console.error('Error submitting order capacity:', error);
        } finally {
            setLoading(false);
        }
    };

    const searchResultColumns: ColumnsType<WCProduct> = [
        {
            title: 'Name',
            dataIndex: ['title', 'rendered'],
            key: 'name',
            render: (_, record) => record.name
        },
        {
            title: 'SKU',
            dataIndex: 'sku',
            key: 'sku',
        },
        {
            title: 'FDA Number',
            key: 'fda_number',
            render: (_, record) => record.meta_data?.find(meta => meta.key === 'fda_number')?.value || '-'
        },
        {
            title: 'Barcode',
            key: 'barcode',
            render: (_, record) => record.barcode || '-'
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleAddProduct(record)}
                    disabled={record.is_deleted}
                >
                    Add
                </Button>
            ),
        },
    ];

    const selectedProductsColumns: ColumnsType<OrderCapacityItem> = [
        {
            title: 'Product Name',
            dataIndex: 'product_name',
            key: 'product_name',
        },
        {
            title: 'Quantity',
            key: 'quantity',
            render: (_, record) => (
                <InputNumber
                    min={1}
                    value={record.quantity}
                    onChange={(value) => handleQuantityChange(record.product_id, value || 1)}
                />
            ),
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button
                    danger
                    onClick={() => handleRemoveProduct(record.product_id)}
                >
                    Remove
                </Button>
            ),
        },
    ];

    return (
        <div className="order-capacity-form">
            <h2>Order Capacity Check</h2>

            <div className="search-section">
                <Input.Group compact style={{ display: 'flex', marginBottom: 20 }}>
                    <Select
                        value={searchType}
                        onChange={(value) => setSearchType(value as SearchType)}
                        style={{ width: 120 }}
                    >
                        <Select.Option value="barcode">Barcode</Select.Option>
                        <Select.Option value="name">Name</Select.Option>
                        <Select.Option value="sku">SKU</Select.Option>
                        <Select.Option value="fda">FDA</Select.Option>
                    </Select>
                    <Input
                        placeholder={`Search by ${searchType}`}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onPressEnter={() => handleSearch(searchText)}
                        style={{ flex: 1 }}
                        disabled={loading}
                    />
                    <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={() => handleSearch(searchText)}
                        loading={loading}
                    >
                        Search
                    </Button>
                </Input.Group>

                <Modal
                    title="Search Results"
                    open={isModalVisible}
                    onCancel={() => {
                        setIsModalVisible(false);
                        setSearchText('');
                        setProducts([]);
                    }}
                    footer={null}
                    width={800}
                >
                    <Table
                        columns={searchResultColumns}
                        dataSource={products}
                        loading={loading}
                        rowKey="id"
                        pagination={false}
                    />
                </Modal>
            </div>

            <div className="selected-products-section">
                <h3>Selected Products</h3>
                <Table
                    columns={selectedProductsColumns}
                    dataSource={selectedProducts}
                    rowKey="product_id"
                    pagination={false}
                />
            </div>

            <div className="submit-section" style={{ marginTop: 20 }}>
                <Button
                    type="primary"
                    onClick={handleSubmit}
                    disabled={selectedProducts.length === 0 || loading}
                    loading={loading}
                >
                    Submit Order Capacity
                </Button>
            </div>
        </div>
    );
};

export default OrderCapacityForm; 